// Fatia S2: página pública /conectar/:token (cliente da agência conecta as próprias contas, sem login).
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Lock } from 'lucide-react'
import { errorMessage } from '../../lib/api'
import { BrandLogo } from '../../features/auth/AuthShell'
import { isDeadLink, isTransient } from '../../features/auth/link-errors'
import { Loading, PlatformLogo } from '../../ui'
import { useLinkInfo, useLinkPending, useLinkSelect, useLinkStart } from '../../features/connections/api'
import SelectAccountDialog from '../../features/connections/SelectAccountDialog'
import { brand } from '../../features/connections/format'
import { oauthErrorMessage } from '../../features/connections/oauthErrors'

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-wrap" style={{ background: 'var(--surface-2)' }}>
      <main className="auth-card stack" style={{ gap: 16, minWidth: 0 }}>
        <BrandLogo />
        <div className="card stack" style={{ gap: 14, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
      </main>
    </div>
  )
}

export default function ConnectLink() {
  const { token = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const pendingId = params.get('selecionar')
  const urlError = oauthErrorMessage(params.get('erro'))
  const justConnected = params.get('conectado')
  const [done, setDone] = useState<boolean>(!!justConnected)

  const info = useLinkInfo(token)
  const start = useLinkStart(token)
  const pending = useLinkPending(token, pendingId)
  const select = useLinkSelect(token, pendingId)

  const clear = (k: string) => {
    const next = new URLSearchParams(params)
    next.delete(k)
    setParams(next, { replace: true })
  }

  if (done) {
    return (
      <Shell>
        <CheckCircle2 size={40} color="var(--green)" />
        <h1 style={{ fontSize: 22 }}>Pronto, conta conectada!</h1>
        <p className="muted">Obrigado! A conta de anúncios já está ligada ao Seu Impulso. Pode fechar esta página: quem enviou o link já consegue ver os resultados.</p>
      </Shell>
    )
  }

  if (info.isLoading) {
    return <Shell><Loading lines={4} /></Shell>
  }

  if (info.error) {
    const gone = isDeadLink(info.error)
    return (
      <Shell>
        <h1 style={{ fontSize: 22 }}>{gone ? 'Este link não vale mais' : 'Não foi possível abrir o link'}</h1>
        <p className="muted">{gone ? 'Ele pode ter expirado, sido cancelado ou estar incompleto. Peça um novo link a quem enviou este convite.' : errorMessage(info.error)}</p>
        {/* tentar de novo só ajuda quando a falha é de rede ou do servidor; link morto não volta */}
        {isTransient(info.error) && <button type="button" className="btn primary" onClick={() => void info.refetch()}>Tentar de novo</button>}
        <Link className="btn" to="/">Página inicial</Link>
      </Shell>
    )
  }

  const data = info.data!
  return (
    <Shell>
      <span className="badge purple" style={{ alignSelf: 'flex-start', whiteSpace: 'normal', maxWidth: '100%' }}>Convite de {data.accountName}</span>
      <h1 style={{ fontSize: 22 }}>Conecte as contas de anúncio de {data.companyName}</h1>
      <p className="muted">{data.accountName} usa o Seu Impulso para acompanhar e melhorar os anúncios. Escolha onde você anuncia e autorize na página oficial. Não precisa criar login.</p>

      {urlError && (
        <div className="form-error row between" role="alert" style={{ gap: 8 }}>
          <span>{urlError}</span>
          <button className="btn ghost sm" onClick={() => clear('erro')}>Fechar</button>
        </div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        {data.providers.length === 0 && <p className="muted">Nenhuma plataforma disponível agora. Tente mais tarde.</p>}
        {data.providers.map((p) => (
          <button key={p.provider} type="button" className="option-card" disabled={start.isPending} onClick={() => start.mutate(p.provider)}>
            <PlatformLogo platform={brand(p.provider)} size="lg" />
            <div style={{ minWidth: 0 }}>
              <div className="strong">{p.name}</div>
              <div className="small muted">{p.description}</div>
            </div>
          </button>
        ))}
      </div>
      {start.error && <div className="form-error" role="alert">{errorMessage(start.error)}</div>}

      <div className="row small muted" style={{ gap: 8, alignItems: 'flex-start' }}>
        <Lock size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>Nunca pedimos sua senha. O dinheiro dos anúncios continua sendo cobrado direto pelas plataformas, no seu cartão. Link válido até {new Date(data.expiresAt).toLocaleDateString('pt-BR')}.</span>
      </div>

      {pendingId && (
        <SelectAccountDialog
          pending={pending.data}
          loading={pending.isLoading}
          error={pending.error}
          submitting={select.isPending}
          submitError={select.error ? errorMessage(select.error) : null}
          onClose={() => clear('selecionar')}
          onSelect={(id) => select.mutate(id, { onSuccess: () => { clear('selecionar'); setDone(true) } })}
        />
      )}
    </Shell>
  )
}
